import React, {ReactNode} from 'react';
import {Button, ButtonGroup, Grid, IconButton, Tooltip} from "@mui/material";
import moment, {Moment} from "moment";
import {GetSalesInvoiceListDto} from "../../model/invoice/sales/GetSalesInvoiceListDto";
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import MyMaterialTable from "../../components/mtable/MyMaterialTable";
import {selectedCompanyStore} from "../../store/SelectedCompany";
import {salesInvoiceApi} from "../../backend/SalesInvoiceApi";
import {formatDate} from "../../utils/DataFormat";
import {formatPrice} from "../../utils/NumberFormat";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import {InvoiceLockedReason} from "../../model/invoice/sales/InvoiceLockedReason";
import ButtonWithConfirmation from "../../components/button/ButtonWithConfirmation";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import {Link} from "react-router-dom";
import {routes} from "../../main/Routes";
import throttle from "lodash.throttle";
import {GetPurchaseInvoiceListDto} from "../../model/invoice/purchase/GetPurchaseInvoiceListDto";
import {purchaseInvoiceApi} from "../../backend/PurchaseInvoiceApi";
import {DatePicker} from "../../components/picker/DatePicker";
import {Trans} from "react-i18next";
import {userInfoStore} from "../../store/UserInfo";
import {UserRole} from "../../model/user/UserRole";

type SalesOrPurchaseInvoice = GetSalesInvoiceListDto | GetPurchaseInvoiceListDto;

export enum InvoiceType {
    SALES, PURCHASE
}

interface IProps {
    type: InvoiceType,
    withActions: boolean,
    fromDate?: Moment,
    toDate?: Moment,
    invoiceSelectionListener?: (selectedInvoices: SalesOrPurchaseInvoice[]) => any
}

interface IState {
    fromDate: Moment,
    toDate: Moment,
    datePickerError: ReactNode
    invoices: SalesOrPurchaseInvoice[],
    loading: boolean,
}

export default class InvoiceList extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            ...this.loadingState(),
            fromDate: props.fromDate ?? moment().clone().startOf('month'),
            toDate: props.toDate ?? moment().clone().endOf('month'),
            datePickerError: ""
        };
    }

    private loadingState() {
        return {
            invoices: [],
            loading: true,
        };
    }

    async componentDidMount() {
        if (selectedCompanyStore.isSelected) {
            this.refreshInvoices();
        }
    }

    private refreshInvoices = throttle(() => {
        this.setState({...this.loadingState()});
        const {fromDate, toDate} = this.state;

        const invoicePromise: Promise<SalesOrPurchaseInvoice[]> = this.isSalesInvoiceList() ?
                salesInvoiceApi.fetchInvoices(fromDate, toDate) :
                purchaseInvoiceApi.fetchInvoices(fromDate, toDate);

        invoicePromise
                .then(response => {
                            this.setState({
                                invoices: response,
                                loading: false
                            })
                        }
                );
    }, 1000, {leading: false, trailing: true})

    private isSalesInvoiceList = () => {
        return this.props.type === InvoiceType.SALES;
    }

    private getLockReason = (lockReason: InvoiceLockedReason) => {
        switch (lockReason) {
            case InvoiceLockedReason.INVOICE_HAS_PAYMENTS:
                return <Trans i18nKey="invoice_list.lock_reason.has_payments">
                    Invoice has payments and can't be deleted.<br/>
                    Please contact your accountant if you want to delete it
                </Trans>
            case InvoiceLockedReason.OLD_INVOICE_CANT_BE_DELETED:
                return <Trans i18nKey="invoice_list.lock_reason.too_old">
                    Old invoices can't be deleted.<br/>
                    Please contact your accountant if you want to delete it
                </Trans>
            case InvoiceLockedReason.INVOICE_CREATED_EXTERNALLY:
                return <Trans i18nKey="invoice_list.lock_reason.created_ext">
                    Invoices created in external system can't be deleted<br/>
                    Please contact your accountant if you want to delete it
                </Trans>
        }
        return "";
    }

    render() {
        const {invoices, loading} = this.state;
        return (
                <>
                    <LoaderWithCompanySelected loading={false}>
                        <MyMaterialTable
                                title={this.renderHeader()}
                                isLoading={loading}
                                columns={[
                                    {title: <Trans i18nKey="invoice_list.columns.invoice_no">Invoice no.</Trans>, field: 'invoiceNo',},
                                    {
                                        title: <Trans i18nKey="invoice_list.columns.date">Date</Trans>,
                                        render: (data) => formatDate(data.documentDate),
                                        field: 'documentDate',
                                        type: 'date'
                                    },
                                    {title: <Trans i18nKey="invoice_list.columns.due">Due</Trans>, render: (data) => formatDate(data.dueDate), field: 'dueDate', type: 'date'},
                                    this.isSalesInvoiceList() ?
                                            {title: <Trans i18nKey="invoice_list.columns.customer">Customer</Trans>, field: 'customerName'} :
                                            {title: <Trans i18nKey="invoice_list.columns.vendor">Vendor</Trans>, field: 'vendorName'},
                                    {
                                        title: <Trans i18nKey="invoice_list.columns.total">Total</Trans>,
                                        render: (data) => formatPrice(data.totalSum, data.currencyCode),
                                        field: 'totalSum',
                                        type: 'numeric'
                                    },
                                    {
                                        title: <Trans i18nKey="invoice_list.columns.paid">Paid</Trans>,
                                        render: (data) => formatPrice(data.paidAmount, data.currencyCode),
                                        field: 'paidAmount',
                                        type: 'numeric'
                                    },
                                ]}
                                data={invoices}
                                options={{
                                    search: true,
                                    sorting: true,
                                    paging: true,
                                    pageSize: 10,
                                    draggable: false,
                                    actionsColumnIndex: 1000,
                                    selection: Boolean(this.props.invoiceSelectionListener),
                                }}

                                onSelectionChange={data => {
                                    this.props.invoiceSelectionListener?.(data)
                                }}
                                actions={!this.props.withActions ? undefined : [
                                    row => ({
                                        icon: () => {
                                            return <>
                                                <Button variant="contained" color="primary" startIcon={<DirectionsRunIcon/>}
                                                        component={Link} to={routes.salesInvoice.replace(':id', row.id)}>
                                                    <Trans i18nKey="common.open">Open</Trans>
                                                </Button>
                                            </>;
                                        },
                                        onClick: () => {
                                        }
                                    }),

                                    row => ({
                                        icon: () => {
                                            const cantBeChanged = row.lockReason !== undefined;
                                            return <>
                                                <ButtonWithConfirmation variant="contained" color="secondary"
                                                                        startIcon={<DeleteIcon/>}
                                                                        disabled={cantBeChanged && userInfoStore.userInfo.currentRole !== UserRole.ACCOUNTANT}
                                                                        onClick={async () => {
                                                                            await salesInvoiceApi.deleteInvoice(row.id);
                                                                            this.refreshInvoices();
                                                                        }}>
                                                    <Trans i18nKey="common.delete_short">Delete</Trans>
                                                </ButtonWithConfirmation>
                                                {cantBeChanged && <Tooltip title={this.getLockReason(row.lockReason)}>
                                                    <IconButton size={"small"}><InfoIcon/></IconButton>
                                                </Tooltip>}
                                            </>;
                                        },
                                        onClick: () => {
                                        }
                                    }),
                                ]}
                        />
                    </LoaderWithCompanySelected>
                </>
        );
    }

    private handleDateChanges = async (from: Moment, to: Moment) => {
        await this.setState({
            fromDate: from,
            toDate: to
        })

        const diff = to.diff(from, 'days');
        if (!diff || diff <= 0) {
            this.setState({
                datePickerError: <Trans i18nKey="invoice_list.invalid_date_range">Invalid dates range</Trans>
            })
        } else if (diff > 365) {
            this.setState({
                datePickerError: <Trans i18nKey="invoice_list.max_allowed_period">Max allowed date range is 365 days</Trans>
            })
        } else {
            this.setState({
                datePickerError: ""
            })
            this.refreshInvoices()
        }
    }

    private renderHeader = () => {
        const {fromDate, toDate, datePickerError} = this.state;

        return <>
            <Grid container spacing={2} justifyContent="flex-start" alignItems="center">
                <Grid item>
                    <DatePicker
                            label={<Trans i18nKey="invoice_list.inv_from">Invoices from</Trans>}
                            value={fromDate}
                            onChange={(date) => this.handleDateChanges(date!!, toDate)}
                            error={datePickerError}
                    />
                </Grid>
                <Grid item>
                    <DatePicker
                            label={<Trans i18nKey="invoice_list.inv_to">Invoices to</Trans>}
                            value={toDate}
                            onChange={(date) => this.handleDateChanges(fromDate, date!!)}
                            error={datePickerError}
                    />
                </Grid>
                <Grid item>
                    <ButtonGroup disableElevation variant="contained">
                        <Button onClick={this.setThisMonth}><Trans i18nKey="invoice_list.period.this_month">This month</Trans></Button>
                        <Button onClick={this.setPrevMonth}><Trans i18nKey="invoice_list.period.prev_month">Previous month</Trans></Button>
                        <Button onClick={this.setThisYear}><Trans i18nKey="invoice_list.period.this_year">This year</Trans></Button>
                        <Button onClick={this.setPrevYear}><Trans i18nKey="invoice_list.period.prev_year">Previous year</Trans></Button>
                    </ButtonGroup>
                </Grid>
            </Grid>
        </>;
    }

    private setThisMonth = async () => {
        await this.handleDateChanges(moment().clone().startOf('month'), moment().clone().endOf('month'))
    }

    private setPrevMonth = async () => {
        const prevMonthStart = moment().clone().startOf('month').subtract(1, 'month');
        await this.handleDateChanges(prevMonthStart, prevMonthStart.clone().endOf('month'));
    }

    private setThisYear = async () => {
        const start = moment().clone().startOf('year');
        await this.handleDateChanges(start, start.clone().endOf('year'));
    }

    private setPrevYear = async () => {
        const start = moment().clone().startOf('year').subtract(1, 'year');
        await this.handleDateChanges(start, start.clone().endOf('year'));
    }
}